import { useState, useCallback, type ReactNode } from 'react';
import ReactModal from 'react-modal';
import { Close } from 'assets/icons';
import { useIntl } from 'react-intl';
import modalStyles from './modalStyles';
import styles from './Modal.module.scss';
import PageTitle from './PageTitle';

interface IModalProps {
  children?: ReactNode;
  isVisible: boolean;
  close: () => void;
  headerText?: ReactNode;
  size?: keyof typeof modalStyles;
  modalStyle?: ReactModal.Styles;
}

// #root is in index.html before the bundle, so it exists when this module runs.
const appElement = document.getElementById('root');
if (appElement) ReactModal.setAppElement(appElement);

/**
 * The modal state; `showModal` keeps what it is called with as `modalProps` (also the click event
 * when it is passed as an `onClick` handler, then `modalProps` is not read).
 */
export const useModal = <TModalProps extends object = object>() => {
  const [isVisible, setIsVisible] = useState(false);
  const [modalProps, setModalProps] = useState<Partial<TModalProps>>({});
  const showModal = useCallback((data: Partial<TModalProps> = {}) => {
    setModalProps(data);
    setIsVisible(true);
  }, []);

  const hideModal = useCallback(() => {
    setIsVisible(false);
    setModalProps({});
  }, []);

  return { showModal, hideModal, modalProps, isVisible };
};

const Modal = ({
  children,
  isVisible,
  close,
  headerText,
  size = 'lg',
  modalStyle = {},
}: IModalProps) => {
  const { formatMessage: t } = useIntl();
  return (
    <ReactModal
      isOpen={isVisible}
      onRequestClose={close}
      style={{ ...modalStyles[size], ...modalStyle }}
      bodyOpenClassName={styles.bodyOpen}
      className={{
        base: styles.modal,
        afterOpen: styles.modalAfterOpen,
        beforeClose: styles.modalBeforeClose,
      }}
      overlayClassName={{
        base: styles.overlay,
        afterOpen: styles.overlayAfterOpen,
        beforeClose: styles.overlayBeforeClose,
      }}
      closeTimeoutMS={300}
    >
      <div className={styles.modalHeader}>
        <PageTitle>{headerText}</PageTitle>
        <button type="button" className={styles.modalCloseButton} onClick={close}>
          <Close title={t({ id: 'alt_text.icons.close' })} />
        </button>
      </div>
      <div className={styles.modalBody}>{children}</div>
    </ReactModal>
  );
};

export default Modal;
